import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOrder } from '../../context/OrderContext';
import { Header } from '../../layout';
import { SFERA_N_KITS, SFERA_N_COMPONENTS } from '../../utils/pensilineFlowRules';
import Swal from 'sweetalert2';

const SferaNOrderForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { dispatch } = useOrder();
  const [tab, setTab] = useState('kits');
  const [selected, setSelected] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [note, setNote] = useState('');

  const allItems = tab === 'kits' ? SFERA_N_KITS : SFERA_N_COMPONENTS;
  const qty = parseInt(quantity, 10) || 0;

  const handleAddToCart = () => {
    if (!selected || qty < 1) return;
    if (selected.needsNote && !note.trim()) return;

    dispatch({
      type: 'ADD_TO_CART',
      payload: {
        productCode: selected.code,
        quantity: qty,
        price: selected.price,
        category: 'pensiline',
        pensilineType: 'sfera-n',
        description: selected.description,
        isSferaNItem: true,
        isStandaloneArticle: true,
        sferaNNote: selected.needsNote ? note : null,
      },
    });

    Swal.fire({
      title: 'Aggiunto al carrello',
      html: `<strong>${selected.code}</strong> &times; ${qty}`,
      icon: 'success',
      showCancelButton: true,
      confirmButtonText: 'Vai al carrello',
      cancelButtonText: 'Aggiungi altro',
      confirmButtonColor: '#cd212a',
    }).then((result) => {
      if (result.isConfirmed) {
        navigate('/order-summary');
      } else {
        setSelected(null);
        setQuantity('');
        setNote('');
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header
        title={location.state?.type === 'offerta' ? 'Richiesta Offerta - Sfera-N' : 'Nuovo Ordine - Sfera-N'}
        backText="Linea"
        backTo="/order-pensiline"
      />
      <div className="container mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex flex-col items-center mb-6">
            <img src="/images/macrocagetorie/Sfera-N_singolo.png" alt="Sfera-N" className="h-20 object-contain mb-3" />
            <h2 className="text-xl font-semibold">SFERA-N</h2>
            <p className="text-gray-600 text-sm">Pensilina con tiranti in acciaio inox AISI 316</p>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-6">
            <button
              onClick={() => { setTab('kits'); setSelected(null); setQuantity(''); setNote(''); }}
              className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === 'kits' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Kit Standard
            </button>
            <button
              onClick={() => { setTab('components'); setSelected(null); setQuantity(''); setNote(''); }}
              className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === 'components' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Componenti Singoli
            </button>
          </div>

          {/* Items */}
          <div className="space-y-3">
            {allItems.map((item) => (
              <button
                key={item.code}
                onClick={() => { setSelected(item); setQuantity(''); setNote(''); }}
                className={`w-full p-4 bg-white rounded-xl shadow hover:shadow-lg transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] text-left ${
                  selected?.code === item.code ? 'ring-2 ring-red-500' : ''
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <span className="font-mono text-sm font-bold text-gray-700">{item.code}</span>
                    <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                    {item.glass && <p className="text-xs text-gray-400 mt-0.5">Vetro: {item.glass}</p>}
                    {item.needsNote && (
                      <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-700">
                        Su misura — specificare lunghezza tirante
                      </span>
                    )}
                  </div>
                  <span className="text-red-600 font-bold text-sm whitespace-nowrap">&euro; {item.price.toFixed(2)}</span>
                </div>
              </button>
            ))}
          </div>

          {/* Quantity + Note + Add */}
          {selected && (
            <div className="mt-6 pt-6 border-t border-gray-200 space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-mono text-sm font-bold">{selected.code}</p>
                <p className="text-sm text-gray-600">{selected.description}</p>
                <p className="text-red-600 font-bold mt-1">&euro; {selected.price.toFixed(2)}/pz</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">QUANTITÀ</label>
                <input
                  type="number"
                  inputMode="numeric"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  min={1}
                  className="w-full p-3 text-base border-2 border-gray-200 rounded-lg focus:ring-1 focus:ring-red-500 focus:border-red-500 outline-none"
                  placeholder="Inserisci la quantità"
                />
                {qty > 0 && (
                  <div className="text-sm text-green-600 bg-green-50 p-2 rounded mt-2">
                    {qty} pz &times; &euro; {selected.price.toFixed(2)} = <strong>&euro; {(qty * selected.price).toFixed(2)}</strong>
                  </div>
                )}
              </div>

              {selected.needsNote && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">LUNGHEZZA TIRANTE</label>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-1 focus:ring-red-500 focus:border-red-500 outline-none resize-none"
                    rows="2"
                    placeholder="Es. tirante 1800mm"
                  />
                </div>
              )}

              <button
                onClick={handleAddToCart}
                disabled={qty < 1 || (selected.needsNote && !note.trim())}
                className="w-full py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
              >
                Aggiungi al carrello
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SferaNOrderForm;
